// src/api/features/customers/queries.ts
import {
  useQuery,
  type QueryKey,
  type QueryClient,
  keepPreviousData,
} from "@tanstack/react-query";

import {
  listCustomers,
  getCustomer,
  getCustomerWallet,
  listCustomerWalletTransactions,
} from "./api";

import type {
  CustomerResult,
  PaginatedCustomers,
  ListCustomersQuery,
  CustomerWallet,
  WalletTransactionsPage,
  WalletTransactionType,
} from "./types";

/* =========================
 * Query Keys
 * ========================= */

export const CustomersKeys = {
  all: ["customers"] as const,
  list: (params: ListCustomersQuery = {}) =>
    ["customers", "list", params] as const,
  detail: (id: number | string) =>
    ["customers", "detail", String(id)] as const,
  wallet: (id: number | string) =>
    ["customers", "wallet", String(id)] as const,
  walletTransactions: (
    id: number | string,
    params: { page?: number; limit?: number; txnType?: WalletTransactionType } = {}
  ) => ["customers", "wallet", "transactions", String(id), params] as const,
} as const;

/* =========================
 * CUSTOMERS — React Query Hooks
 * ========================= */

/** 🔹 List customers with pagination + filters */
export function useCustomers(params: ListCustomersQuery = {}) {
  const key = CustomersKeys.list(params);

  return useQuery<PaginatedCustomers>({
    queryKey: key,
    queryFn: async () => {
      // console.log("🔍 Fetching customers with params:", params);
      const res = await listCustomers(params);
      // console.log("✅ Customers fetched:", res);
      return res;
    },
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });
}

/** 🔹 Get a single customer */
export function useCustomer(id: number | string | null | undefined) {
  const enabled = id !== null && id !== undefined && id !== "";
  // console.log("[useCustomer] called with id =", id, "enabled =", enabled);

  const detailKey: QueryKey = enabled
    ? CustomersKeys.detail(id as number | string)
    : (["customers", "detail", "disabled"] as QueryKey);

  return useQuery<CustomerResult>({
    queryKey: detailKey,
    queryFn: async () => {
      // console.log("🔍 [useCustomer] queryFn, id =", id);
      const res = await getCustomer(id as number | string);
      // console.log("✅ [useCustomer] queryFn result =", res);
      return res;
    },
    enabled,
    staleTime: 60_000,
    gcTime: 10 * 60_000,
  });
}


/* =========================
 * Prefetch helpers (optional)
 * ========================= */

export function prefetchCustomers(
  qc: QueryClient,
  params: ListCustomersQuery = {}
) {
  return qc.prefetchQuery({
    queryKey: CustomersKeys.list(params),
    queryFn: async () => {
      // console.log("🔍 Prefetch customers:", params);
      const res = await listCustomers(params);
      // console.log("✅ Prefetched customers:", res);
      return res;
    },
    staleTime: 30_000,
  });
}

export function prefetchCustomer(qc: QueryClient, id: number | string) {
  return qc.prefetchQuery({
    queryKey: CustomersKeys.detail(id),
    queryFn: async () => {
      // console.log(`🔍 Prefetch customer id=${id}`);
      const res = await getCustomer(id);
      // console.log("✅ Prefetched customer:", res);
      return res;
    },
    staleTime: 60_000,
  });
}

export function useCustomerWallet(id: number | string | null | undefined) {
  const enabled = id !== null && id !== undefined && id !== "";
  const key: QueryKey = enabled
    ? CustomersKeys.wallet(id as number | string)
    : (["customers", "wallet", "disabled"] as QueryKey);

  return useQuery<CustomerWallet>({
    queryKey: key,
    queryFn: async () => getCustomerWallet(id as number | string),
    enabled,
    staleTime: 20_000,
    gcTime: 5 * 60_000,
  });
}

export function useCustomerWalletTransactions(
  id: number | string | null | undefined,
  params: { page?: number; limit?: number; txnType?: WalletTransactionType } = {}
) {
  const enabled = id !== null && id !== undefined && id !== "";
  const key: QueryKey = enabled
    ? CustomersKeys.walletTransactions(id as number | string, params)
    : (["customers", "wallet", "transactions", "disabled"] as QueryKey);

  return useQuery<WalletTransactionsPage>({
    queryKey: key,
    queryFn: async () =>
      listCustomerWalletTransactions(id as number | string, params),
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 10_000,
    gcTime: 2 * 60_000,
  });
}
